// components/shared/forms/DynamicForm.tsx

import { FieldConfig } from '../../types/core/JsonParse';

interface DynamicFormProps {
    fields: FieldConfig[];
    onChange: (key: string, value: any) => void;
    values: Array<{ key: string; label: string; value: any }>;
}

export function DynamicForm({ fields, onChange, values }: DynamicFormProps) {
    return (
        <div className="space-y-4">
            {fields.map((field) => {
                // Trouver la valeur dans le tableau
                const item = values.find(v => v.key === field.key);
                const value = item?.value;
                const label = item?.label || field.key;

                return (
                    <FieldRenderer
                        key={field.key}
                        field={{ ...field, label }}
                        value={value}
                        onChange={(val) => onChange(field.key, val)}
                    />
                );
            })}
        </div>
    );
}

function FieldRenderer({ field, value, onChange }: { field: FieldConfig; value: any; onChange: (val: any) => void }) {
    switch (field.type) {
        case 'text':
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                    </label>
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                    />
                </div>
            );

        case 'textarea':
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                    </label>
                    <textarea
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                    />
                </div>
            );

        case 'number':
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                    </label>
                    <input
                        type="number"
                        value={value || 0}
                        onChange={(e) => onChange(parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                    />
                </div>
            );

        case 'boolean':
            return (
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={value || false}
                        onChange={(e) => onChange(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <label className="text-sm font-medium text-gray-700">{field.label}</label>
                </div>
            );

        case 'image':
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                    </label>
                    <div className="flex items-center gap-4">
                        {value && (
                            <img src={value} alt={field.label} className="w-24 h-24 object-cover rounded-lg" />
                        )}
                        <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="URL de l'image"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                </div>
            );

        case 'list':
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                    </label>
                    <div className="space-y-2">
                        {Array.isArray(value) && value.map((item, index) => (
                            <div key={index} className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg">
                                {field.children ? (
                                    <div className="flex-1 grid grid-cols-1 gap-2">
                                        {field.children.map((childField) => (
                                            <FieldRenderer
                                                key={childField.key}
                                                field={{
                                                    ...childField,
                                                    label: childField.label || childField.key,
                                                }}
                                                value={item[childField.key]}
                                                onChange={(val) => {
                                                    const newList = [...value];
                                                    newList[index] = { ...newList[index], [childField.key]: val };
                                                    onChange(newList);
                                                }}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => {
                                            const newList = [...value];
                                            newList[index] = e.target.value;
                                            onChange(newList);
                                        }}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newList = value.filter((_: any, i: number) => i !== index);
                                        onChange(newList);
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            const newItem = field.children
                                ? field.children.reduce((acc, child) => ({ ...acc, [child.key]: '' }), {})
                                : '';
                            onChange([...(value || []), newItem]);
                        }}
                        className="mt-2 px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        + Ajouter
                    </button>
                </div>
            );

        case 'object':
            return (
                <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-700 mb-3">{field.label}</h3>
                    <div className="space-y-3">
                        {field.children && (
                            <DynamicForm
                                fields={field.children}
                                values={Object.entries(value || {}).map(([k, v]) => ({ key: k, label: k, value: v }))}
                                onChange={(key, val) => {
                                    onChange({ ...(value || {}), [key]: val });
                                }}
                            />
                        )}
                    </div>
                </div>
            );

        default:
            return null;
    }
}